import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams, Link } from 'react-router-dom';
import { ShoppingBag } from 'lucide-react';
import { getCourier, getCourierSetting } from '../../state/courierSlice';
import { useAuth, useEmailVerification, usePasskeyAuth } from '../hooks';
import LoginForm from './LoginForm';
import CreateAccountForm from './CreateAccountForm';
import PasswordResetForm from './PasswordResetForm';
import WhatsAppSignIn from './WhatsAppSignIn';
import SmsSignIn from './SmsSignIn';
import { OTPInput, Carousel } from '@shared/components';
import { AccessToken } from '@shared/sdk-v1/models';

type AuthMode = 'login' | 'create' | 'password-reset' | 'whatsapp' | 'sms' | 'email-verification';

const AuthPage = () => {
  const courier = useSelector(getCourier);
  const courierSetting = useSelector(getCourierSetting);
  const { handleLogin, isLoading: isLoggingIn, error: loginError, clearError } = useAuth();
  // Owned here, not in LoginForm — a second hook instance would hold state
  // nothing ever updates (see the note on LoginFormProps).
  const {
    signInWithPasskey,
    isSupported: isPasskeySupported,
    isLoading: isPasskeyLoading,
  } = usePasskeyAuth();
  const { sendEmailVerification, verifyEmailCode, isLoading: verificationLoading, isSendingVerification } = useEmailVerification();
  const [searchParams] = useSearchParams(); 
  const referralId = searchParams.get('referralId');
  const signupParam = searchParams.get('signup');
  const initialEmail = searchParams.get('email') || '';
  const source = searchParams.get('source') || '';
  const tier = searchParams.get('tier') || '';
  const bundle = searchParams.get('bundle') || '';
  const businessName = searchParams.get('businessName') || '';
  const contactName = searchParams.get('contactName') || '';
  const initialPhone = searchParams.get('phone') || '';
  const shouldStartInSignup = !!(referralId || signupParam === '1');

  const [authMode, setAuthMode] = useState<AuthMode>(shouldStartInSignup ? 'create' : 'login');
  const [isSignIn, setIsSignIn] = useState(!shouldStartInSignup);
  const [userEmail, setUserEmail] = useState('');
  const [userId, setUserId] = useState('');
  const [pendingAccessToken, setPendingAccessToken] = useState<AccessToken | null>(null);
       const version = import.meta.env.VITE_APP_VERSION || 'dev';
  const hostname = window.location.hostname;
  const hostParts = hostname.split('.');
  const homeUrl = hostParts.length >= 3
    ? `${window.location.protocol}//${hostParts.slice(1).join('.')}`
    : (courier?.webUrl || '/');

  const brandInfo = {
    logo: courier?.imageUrl,
    name: courier?.name,
    tagline: courier?.slogan,
    primaryColor: '#1f2937',
    environment: version,
        carouselImages: [
      'https://ik.imagekit.io/47zhisylvftkf/csr_HJHiQ4L4ZA.png?updatedAt=1754429580603',
      'https://ik.imagekit.io/47zhisylvftkf/smiling-man_JsUfqIXoa.png?updatedAt=1754429221531',
      'https://ik.imagekit.io/47zhisylvftkf/smiling-woman_KFTBZxPgZ.png?updatedAt=1754429221527'
    ]
  };

  // Handle login with potential email verification
  const handleLoginWithVerification = async (email: string, password: string, turnstileToken?: string, turnstileSiteKey?: string) => {
    const result = await handleLogin(email, password, turnstileToken, turnstileSiteKey);

    if (result.needsVerification) {
      setUserEmail(result.userEmail!);
      setUserId(result.userId!);
      setPendingAccessToken(result.accessToken!);
      setAuthMode('email-verification');
      // Automatically send verification code
      await sendEmailVerification(result.userId!);
    }
  };

  const handleForgotPassword = () => {
    setAuthMode('password-reset');
  };

  const handleBackToLogin = () => {
    setAuthMode('login');
    setIsSignIn(true);
    setPendingAccessToken(null);
    clearError();
  };

  const handleToggleAuthMode = () => {
    setIsSignIn(!isSignIn);
    setAuthMode(isSignIn ? 'create' : 'login');
    clearError();
  };

  const handleWhatsAppSignIn = () => {
    setAuthMode('whatsapp');
  };

  const handleSmsSignIn = () => {
    setAuthMode('sms');
  };

  const handleEmailVerification = async (code: string) => {
    const result = await verifyEmailCode(code, userId, pendingAccessToken || undefined);
    if (result.success) {
      setPendingAccessToken(null);
      // Don't set auth mode back to login - let the app handle the redirect
      // since the user is now authenticated
    }
  };

  const handleResendEmailVerification = async () => {
    if (!isSendingVerification) {
      await sendEmailVerification(userId);
    }
  };

  const renderAuthForm = () => {
    switch (authMode) {
      case 'password-reset':
        return (
          <PasswordResetForm
            onBackToLogin={handleBackToLogin}
            primaryColor={brandInfo.primaryColor}
          />
        );

      case 'whatsapp':
        return (
          <WhatsAppSignIn
            onBack={handleBackToLogin}
            primaryColor={brandInfo.primaryColor}
          />
        );

      case 'sms':
        return (
          <SmsSignIn
            onBack={handleBackToLogin}
            primaryColor={brandInfo.primaryColor}
          />
        );

      case 'email-verification':
        return (
          <OTPInput
            onComplete={handleEmailVerification}
            onResend={handleResendEmailVerification}
            title="Verify your email address"
            description={`We've sent a verification code to ${userEmail}. Please enter the code below to verify your account. If you don't see it, check your spam or junk folder.${courierSetting?.requireVerification ? ' This verification is required by your courier.' : ''}`}
            verifyText="Verify Email"
            primaryColor={brandInfo.primaryColor}
            isLoading={verificationLoading}
            disabled={isSendingVerification}
            isResending={isSendingVerification}
          />
        );

      case 'create':
        return (
          <CreateAccountForm
            onToggleMode={handleToggleAuthMode}
            primaryColor={brandInfo.primaryColor}
            referralId={referralId}
            initialEmail={initialEmail}
            initialPhone={initialPhone}
            source={source}
            tier={tier}
            bundle={bundle}
            businessName={businessName}
            contactName={contactName}
          />
        );

      case 'login':
      default:
        return (
          <LoginForm
            onForgotPassword={handleForgotPassword}
            onWhatsAppSignIn={handleWhatsAppSignIn}
            onSmsSignIn={handleSmsSignIn}
            onToggleMode={handleToggleAuthMode}
            onLogin={handleLoginWithVerification}
            primaryColor={brandInfo.primaryColor}
            isLoading={isLoggingIn}
            error={loginError}
            onClearError={clearError}
            onPasskeySignIn={signInWithPasskey}
            isPasskeySupported={isPasskeySupported}
            isPasskeyLoading={isPasskeyLoading}
          />
        );
    }
  };

  return (
    <div className="flex flex-col md:flex-row w-full h-screen max-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Branding Section */}
      <div
        className="w-full md:w-1/2 relative flex flex-col items-center justify-center text-white overflow-hidden h-[20vh] min-[390px]:h-[35vh] md:h-screen"
        style={{ backgroundColor: brandInfo.primaryColor }}
      >
        {/* Background Carousel - hidden on very small devices */}
        <div className="absolute inset-0 hidden min-[390px]:block">
          <Carousel 
            images={brandInfo.carouselImages} 
            className="w-full h-full"
            imagePosition="top"
            objectFit="cover"
          />
          {/* Dark overlay - lighter on mobile for better image visibility */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/20 md:bg-black/50" />
        </div>

        {/* Content - adjusted for better mobile positioning */}
        <div className="relative z-10 max-w-md text-center p-2 md:p-8">
          {brandInfo.logo && (
            <div className="mb-1 md:mb-4 flex justify-center">
              <a href={homeUrl} className="cursor-pointer">
                <div className="bg-white rounded-full p-2 w-12 h-12 md:p-4 md:w-24 md:h-24 flex items-center justify-center">
                  <img
                    src={brandInfo.logo}
                    alt={`${brandInfo.name} logo`}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              </a>
            </div>
          )}
          <h1 className="text-base md:text-3xl font-bold mb-0.5 md:mb-2">{brandInfo.name}</h1>
          {brandInfo.tagline && <p className="text-xs md:text-lg opacity-90 hidden min-[390px]:block">{brandInfo.tagline}</p>}
          {courierSetting?.isShopEnabled && (
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 mt-2 md:mt-4 px-4 py-2 md:px-6 md:py-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white rounded-full text-xs md:text-base font-medium transition-colors"
            >
              <ShoppingBag className="h-3.5 w-3.5 md:h-5 md:w-5" />
              Visit Shop
            </Link>
          )}
        </div>
      </div>

      {/* Form Section */}
      <div className="w-full md:w-1/2 h-[80vh] min-[390px]:h-[65vh] md:h-screen overflow-y-auto">
        {/* Centering wrapper: min-h-full centers short forms but grows past the
            container when the form is tall, so the outer div scrolls and every
            field stays reachable (top-aligned on mobile, centered on desktop). */}
        <div className="flex flex-col items-center justify-start md:justify-center min-h-full pt-8 px-4 pb-4 md:p-8">
          {renderAuthForm()}
        </div>

        {/* Environment indicator */}
        {brandInfo.environment && (
          <div className="absolute bottom-2 right-2">
            <span className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-200 dark:text-gray-300 dark:bg-gray-700 rounded">
              {brandInfo.environment}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthPage;
